import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const CONSENT_KEY = "aidoctor-consent";
const CONSENT_VERSION = "2026-07";

export const getConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY));
  } catch {
    return null;
  }
};

const Toggle = ({ on, onClick, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onClick}
    data-testid={testid}
    className={`relative w-12 h-7 border transition-colors duration-300 ${on ? "bg-[#5CCBF5] border-[#5CCBF5]" : "bg-[#131C31] border-white/10"}`}
  >
    <span className={`absolute top-1 w-4 h-4 transition-all duration-300 ${on ? "left-6 bg-[#0E1526]" : "left-1 bg-[#5c5c58]"}`} />
  </button>
);

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    window.__aidoctorConsent = existing;
    if (!existing || existing.version !== CONSENT_VERSION) setShowBanner(true);
    const open = () => {
      const c = getConsent();
      setAnalytics(!!c?.analytics);
      setAdvertising(!!c?.advertising);
      setShowBanner(false);
      setShowSettings(true);
    };
    window.addEventListener("open-cookie-settings", open);
    return () => window.removeEventListener("open-cookie-settings", open);
  }, []);

  const save = (choice, a, m) => {
    const record = {
      version: CONSENT_VERSION,
      choice,
      essential: true,
      analytics: a,
      advertising: m,
      ts: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    window.__aidoctorConsent = record;
    setShowBanner(false);
    setShowSettings(false);
  };

  const btnBase = "h-11 px-6 font-mono text-[11px] tracking-[0.16em] uppercase transition-colors duration-300";
  const btnSolid = `${btnBase} bg-[#f5f3ee] text-[#0E1526] hover:bg-[#5CCBF5]`;
  const btnOutline = `${btnBase} border border-white/20 text-[#f5f3ee] hover:border-[#5CCBF5] hover:text-[#5CCBF5]`;

  return (
    <>
      {showBanner && (
        <div data-testid="cookie-banner" className="fixed bottom-0 inset-x-0 z-[70] border-t border-white/10 bg-[#0E1526]/97 backdrop-blur">
          <div className="shell py-6 flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
            <p className="text-[#a7a7a2] text-sm max-w-2xl leading-relaxed">
              This website uses only <span className="text-[#f5f3ee]">strictly necessary storage</span> — no
              advertising or analytics cookies are currently installed, and none will load without your consent
              if that ever changes. Read the{" "}
              <Link to="/cookies" className="text-[#5CCBF5] underline underline-offset-4">Cookie Policy</Link>.
            </p>
            <div className="flex flex-wrap gap-3 shrink-0">
              <button type="button" data-testid="cookie-reject" onClick={() => save("rejected", false, false)} className={btnSolid}>Reject</button>
              <button type="button" data-testid="cookie-customize" onClick={() => { setShowBanner(false); setShowSettings(true); }} className={btnOutline}>Customize</button>
              <button type="button" data-testid="cookie-accept" onClick={() => save("accepted", true, true)} className={btnSolid}>Accept</button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#0E1526]/80 backdrop-blur-sm" data-testid="cookie-settings-modal">
          <div className="w-full max-w-lg border border-white/10 bg-[#0E1526] p-8 lg:p-10 max-h-[90vh] overflow-y-auto">
            <p className="eyebrow">Cookie Settings</p>
            <h2 className="display text-2xl text-[#f5f3ee] mt-4">Your choice, recorded.</h2>
            <p className="mt-4 text-[#a7a7a2] text-sm leading-relaxed">
              Stored on this device under policy version {CONSENT_VERSION}. Change or withdraw anytime from the
              Cookie Settings link in the footer.
            </p>
            <div className="mt-8 border-t border-white/10">
              <div className="py-5 border-b border-white/10 flex items-start justify-between gap-6">
                <div>
                  <p className="text-[#f5f3ee] font-medium">Strictly necessary</p>
                  <p className="text-[#5c5c58] text-sm mt-1">Site function, hosting, security, and remembering this choice. Always active.</p>
                </div>
                <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[#5CCBF5] mt-1">Always on</span>
              </div>
              <div className="py-5 border-b border-white/10 flex items-start justify-between gap-6">
                <div>
                  <p className="text-[#f5f3ee] font-medium">Analytics</p>
                  <p className="text-[#5c5c58] text-sm mt-1">Not currently in use — no analytics cookies are installed on this website.</p>
                </div>
                <Toggle on={analytics} onClick={() => setAnalytics(!analytics)} testid="cookie-toggle-analytics" />
              </div>
              <div className="py-5 border-b border-white/10 flex items-start justify-between gap-6">
                <div>
                  <p className="text-[#f5f3ee] font-medium">Advertising</p>
                  <p className="text-[#5c5c58] text-sm mt-1">Not currently in use — no advertising pixels or conversion tags are installed.</p>
                </div>
                <Toggle on={advertising} onClick={() => setAdvertising(!advertising)} testid="cookie-toggle-advertising" />
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button type="button" data-testid="cookie-save" onClick={() => save("custom", analytics, advertising)} className={btnSolid}>Save my choices</button>
              <button type="button" data-testid="cookie-reject-all" onClick={() => save("rejected", false, false)} className={btnOutline}>Reject all</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
